/** @format */

import ChevronRight from '@/assets/icons/joivy_icon_chevron_right_light.svg';
import { Icon } from '@/components/ui/Icon';
import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import SwiperCore, { A11y, Lazy, Navigation, Pagination, Scrollbar, Swiper as SwiperClass } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.min.css';
import 'swiper/swiper.min.css';
import { SwiperButtonNext, SwiperButtonPrev, SwiperCounter, SwiperPhoto, Wrapper } from './GalleryCarousel.style';
import { GalleryCarouselDots } from './GalleryCarouselDots';

// Import Swiper styles
import { TTranslation } from '@/i18n/createProvider';
import { useAppTranslation } from '@/i18n/declarations';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import { useCardRoomContext } from './context';
import { LoadingSpinner } from './LoadingSpinner';

// install Swiper modules
SwiperCore.use([Navigation, Pagination, Scrollbar, A11y, Lazy]);

type CarouselProps = {
  photos: string[];
  isPopup?: boolean;
};

const GalleryCarousel: React.FC<CarouselProps> = React.memo(({ photos, isPopup }) => {
  const { t } = useAppTranslation();
  const { room: { code = '' } = {} } = useCardRoomContext();
  const [loadedImages, setLoadedImages] = useState<Record<number, boolean>>({});
  const [isNavigating, setIsNavigating] = useState<boolean>(false);
  const [allImagesLoaded, setAllImagesLoaded] = useState<boolean>(false);
  const sectionName = useRef<string>(isPopup ? 'popup' : 'listing');
  const swiperRef = useRef<SwiperClass | null>(null);
  const [currentSlide, setCurrentSlide] = useState<number>(1);
  const timer = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (photos && photos.length > 0) {
      const imagesToPreload = Math.min(4, photos.length);
      let loadedCount = 0;

      for (let i = 0; i < imagesToPreload; i++) {
        const img = new Image();
        img.src = photos[i];
        img.onload = () => {
          setLoadedImages(prev => ({ ...prev, [i]: true }));
          loadedCount++;

          if (loadedCount === imagesToPreload) {
            setAllImagesLoaded(true);
          }
        };
      }
    }
  }, [photos]);

  const { memoizedPhotos, photosLength } = useMemo(
    () => ({
      memoizedPhotos: [photos[photos.length - 1], ...photos, photos[0]],
      photosLength: photos.length,
    }),
    [photos],
  );

  const updateSlide = useCallback(
    ({ realIndex }: SwiperClass) => {
      let newIndex = realIndex;

      if (realIndex === 0) {
        newIndex = photos.length;
      } else if (realIndex === photosLength + 1) {
        newIndex = 1;
      }

      if (newIndex !== realIndex) {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
          slideTo(newIndex, 0);
          timer.current = undefined;
        }, 300);
      }

      setCurrentSlide(newIndex);

      const currentImageIndex = newIndex - 1;
      if (!loadedImages[currentImageIndex] && currentImageIndex >= 0 && currentImageIndex < photos.length) {
        const img = new Image();
        img.src = photos[currentImageIndex];
        img.onload = () => {
          setLoadedImages(prev => ({ ...prev, [currentImageIndex]: true }));
        };
      }
    },
    [photos.length, photosLength, loadedImages],
  );

  const handleSlide = useCallback(
    (step: number) => {
      if (!!swiperRef.current && !timer.current) {
        const { realIndex } = swiperRef.current;
        slideTo(realIndex + step);

        timer.current = setTimeout(() => {
          timer.current = undefined;
        }, 300);
      }
    },
    [swiperRef.current, memoizedPhotos],
  );
  const handleSlideClick = useCallback(
    (direction: 'left' | 'right', evt: React.MouseEvent) => {
      if (isNavigating) return;

      evt.stopPropagation();
      evt.preventDefault();

      setIsNavigating(true);
      handleSlide(direction === 'left' ? -1 : 1);

      setTimeout(() => {
        setIsNavigating(false);
      }, 350);
    },
    [handleSlide, isNavigating],
  );

  const slideTo = (idx: number, duration?: number) => {
    swiperRef.current?.slideTo(idx, duration);
  };

  return (
    <Wrapper>
      <Swiper
        id={`swiper-${code}-${sectionName.current}`}
        spaceBetween={0}
        slidesPerView={1}
        updateOnImagesReady
        watchSlidesProgress
        loop={false}
        preloadImages
        onSlideChange={updateSlide}
        lazy={{
          loadPrevNextAmount: 1,
          loadOnTransitionStart: true,
          loadPrevNext: true,
          checkInView: true,
          enabled: true,
        }}
        onSwiper={swiper => (swiperRef.current = swiper)}
        initialSlide={1}
      >
        {memoizedPhotos.map((photo, index) => {
          const adjustedIndex = index - 1; // Adjust index for loadedImages check
          return (
            <SwiperSlide key={index + code + sectionName.current}>
              <SwiperPhoto
                fetchpriority={index <= 2 ? 'high' : 'low'}
                loading={index <= 4 ? 'eager' : 'lazy'}
                decoding="async"
                src={photo}
                alt={`${t('plp.alt_image' as TTranslation)}${index + 1} - ${code}`}
                className=""
                style={
                  adjustedIndex >= 0 && !loadedImages[adjustedIndex] && !allImagesLoaded ? { visibility: 'hidden' } : {}
                }
              />
              {adjustedIndex >= 0 && !loadedImages[adjustedIndex] && !allImagesLoaded && <LoadingSpinner />}
            </SwiperSlide>
          );
        })}
      </Swiper>

      {isPopup && <GalleryCarouselDots currentSlide={currentSlide} totalPhotos={photosLength} />}

      <SwiperCounter>
        <span>
          {currentSlide}/{photosLength}
        </span>
      </SwiperCounter>
      <SwiperButtonNext onClick={evt => handleSlideClick('right', evt)} aria-label={t(`aria_label_text.next`)}>
        <Icon icon={ChevronRight} color={'#101828'} width={24} height={24} />
      </SwiperButtonNext>
      <SwiperButtonPrev onClick={evt => handleSlideClick('left', evt)} aria-label={t(`aria_label_text.previous`)}>
        <Icon icon={ChevronRight} color={'#101828'} width={24} height={24} />
      </SwiperButtonPrev>
    </Wrapper>
  );
});

export default GalleryCarousel;
